import React, { forwardRef, memo } from 'react';
import styled from '@emotion/styled';
import { useButton } from '~Hooks';
import { CART_BAR_HEIGHT } from './CartBar';
import { CartLineEdit } from '../../types';
import CartContents from './CartContents';

/** How long the open/close slide takes. */
export const CART_DRAWER_TRANSITION_MS = 450;

/**
 * Single root for the backdrop and the drawer. Mounted for the whole portrait
 * menu, not just while open - Menu toggles the `is-open` class on it via a
 * ref, so opening the cart only plays a transition instead of first having to
 * mount and render the whole cart contents. Above CartBar (z-index 3) - the
 * open drawer, and the cart lines inside it, must never end up rendered
 * underneath the checkout button sitting right below it.
 */
const Root = styled.div`
  position: absolute;
  inset: 0;
  z-index: 4;
  pointer-events: none;
  /* Closed, the drawer is tucked under the bottom edge but would still be
     painted: its shadow showed above the CartBar and every cart change
     repainted it for nothing. Hidden while closed instead - the delay keeps it
     visible for the close slide, and opening shows it at once. */
  visibility: hidden;
  transition: visibility 0s linear ${CART_DRAWER_TRANSITION_MS}ms;

  &.is-open {
    visibility: visible;
    transition-delay: 0s;
  }

  /* Only the backdrop and the sheet take taps - the root itself stays
     transparent to hits, so the CartBar under its bottom strip keeps working. */
  &.is-open > * {
    pointer-events: auto;
  }
`;

/**
 * Dims the menu behind the drawer. Tapping it closes the cart. Stops at the
 * CartBar, which stays undimmed and tappable - "Continue to payment" must
 * proceed from an open cart exactly as it does from a closed one.
 */
const Backdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: ${CART_BAR_HEIGHT}px;
  background: rgba(0, 0, 0, 0.45);
  opacity: 0;
  transition: opacity ${CART_DRAWER_TRANSITION_MS}ms ease-out;

  .is-open & {
    opacity: 1;
  }
`;

/** Sits right on top of the bar, full width, tucked behind it until opened. */
const Drawer = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  /* The zoom below scales this offset too - divide it back out so the sheet
     sits exactly on the bar with no gap. */
  bottom: calc(${CART_BAR_HEIGHT}px / var(--eposCartDrawerZoom));
  max-height: 85%;
  /* Everything inside scales together - see --eposCartDrawerZoom. */
  zoom: var(--eposCartDrawerZoom);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 32px 32px 0 0;
  background: var(--eposSurface);
  color: var(--eposText);
  font-family: var(--eposFontFamily);
  box-shadow: 0 -24px 60px rgba(0, 0, 0, 0.35);
  transform: translate3d(0, calc(100% + ${CART_BAR_HEIGHT}px / var(--eposCartDrawerZoom)), 0);
  transition: transform ${CART_DRAWER_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;

  .is-open & {
    transform: translate3d(0, 0, 0);
  }
`;

interface Props {
  onClose: () => void;
  /** "Ändra"/"Edit" on a cart line - opens ItemDetail prefilled with its picks. */
  onEditLine: (edit: CartLineEdit) => void;
  /** The line being edited, if any. */
  editing?: CartLineEdit;
}

/**
 * Portrait shell for the cart: a dimming backdrop and a sheet that slides up
 * over the CartBar. Holds no cart logic of its own - that is CartContents,
 * shared with the landscape CartPanel. The ref is the node Menu toggles
 * `is-open` on to show and hide it.
 */
const CartDrawer = forwardRef<HTMLDivElement, Props>(({ onClose, onEditLine, editing }, ref) => {
  const { touching, disabled, disabledToast, ...backdrop } = useButton({ onClick: onClose });

  return (
    <Root ref={ref}>
      <Backdrop {...backdrop} data-testid="epos-cart-backdrop" />
      <Drawer data-testid="epos-cart-drawer">
        <CartContents onEditLine={onEditLine} editing={editing} />
      </Drawer>
    </Root>
  );
});

CartDrawer.displayName = 'CartDrawer';

export default memo(CartDrawer);
